import { calculatePaymentForApplication } from "../services/paymentsService";
import { PresentationWithId } from "../interfaces/IPresentations";
import { useApplicationContext } from "../contexts/ApplicationContext";
import { useEffect, useMemo, useState } from "react";
import { useLoggingActions } from "./useLogging";
/**
 * ============================================================================
 * PRESENTATIONS HOOK - HOOK ZARZĄDZANIA PREZENTACJAMI
 * ============================================================================
 *
 * CEL HOOKA:
 * - Zarządza operacjami CRUD na prezentacjach (Create, Read, Update, Delete)
 * - Obsługuje operacje wsadowe dla kategorii SOLO
 * - Zapewnia spójność danych i poprawność obliczeń kwot
 * - Zarządza stanem ładowania i błędów
 *
 * ARCHITEKTURA:
 * - Custom Hook Pattern: Enkapsuluje logikę zarządzania prezentacjami
 * - State Management: Zarządza lokalnym stanem prezentacji
 * - Batch Operations: Obsługuje operacje wsadowe dla SOLO
 * - Error Handling: Obsługuje błędy z logowaniem
 * - Data Consistency: Zapewnia spójność między lokalnym stanem a bazą danych
 *
 * PRZEPŁYW DANYCH:
 * 1. Pobiera prezentacje z bazy na podstawie filtrów (categoryId, applicationId)
 * 2. Zarządza lokalnym stanem prezentacji
 * 3. Obsługuje operacje CRUD z aktualizacją mapy uczestników
 * 4. Przelicza kwoty zgłoszenia po każdej zmianie
 * 5. Loguje operacje dla audytu
 * 6. Obsługuje operacje wsadowe dla kategorii SOLO
 *
 * SPECYFIKA SOLO:
 * - Operacje wsadowe - wszystkie zmiany w jednej transakcji
 * - Automatyczne odświeżanie danych z bazy po operacjach
 * - Poprawne obliczanie mapy uczestników i kwot
 * - Obsługa duplikatów kluczy React
 */

import {
  createPresentationAndUpdateParticipantsCounter,
  deletePresentationAndUpdateParticipantsCounter,
  findAllPresentationsByQuery,
  updatePresentationAndParticipantsCounter,
  WhereCondition,
} from "../utils/firestore";

// Interfejs dla wyniku operacji
interface OperationResult {
  success: boolean;
  error?: string;
  presentation?: PresentationWithId;
}

type UsePresentationsOptions = {
  applicationId?: string;
  categoryId?: string;
};

/**
 * ============================================================================
 * HOOK: usePresentations
 * ============================================================================
 * 
 * GŁÓWNA FUNKCJA HOOKA:
 * - Zarządza operacjami CRUD na prezentacjach
 * - Obsługuje operacje wsadowe dla kategorii SOLO
 * - Zapewnia spójność danych i poprawność obliczeń kwot
 * - Zarządza stanem ładowania i błędów
 * 
 * PARAMETRY WEJŚCIOWE:
 * - categoryId: ID kategorii (opcjonalne - filtruje prezentacje)
 * - applicationId: ID zgłoszenia (opcjonalne - filtruje prezentacje)
 * 
 * ZWRACA:
 * - presentations: Lista prezentacji
 * - isLoading: Stan ładowania
 * - handlePresentationAdd: Funkcja dodawania prezentacji
 * - handlePresentationUpdate: Funkcja aktualizacji prezentacji
 * - handlePresentationDelete: Funkcja usuwania prezentacji
 * - handleSoloPresentationsBatch: Funkcja operacji wsadowych SOLO
 * 
 * SPECYFIKA SOLO:
 * - Operacje wsadowe - wszystkie zmiany w jednej transakcji
 * - Automatyczne odświeżanie danych z bazy po operacjach
 * - Poprawne obliczanie mapy uczestników i kwot
 */
export const usePresentations = ({ categoryId, applicationId }: UsePresentationsOptions = {}) => {
  // ============================================================================
  // STAN LOKALNY I KONTEKSTY
  // ============================================================================
  // Zarządza lokalnym stanem prezentacji i pobiera dane z kontekstów
  
  const [presentations, setPresentations] = useState<PresentationWithId[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logPresentationCreate, logPresentationUpdate, logPresentationDelete } =
    useLoggingActions();
  const { application, updateApplication } = useApplicationContext();

  // Memoizacja whereConditions aby uniknąć problemów z wydajnością
  const whereConditions: WhereCondition[] = useMemo(() => {
    const conditions: WhereCondition[] = [];
    if (categoryId) conditions.push({ field: "categoryId", operator: "==", value: categoryId });
    if (applicationId) conditions.push({ field: "applicationId", operator: "==", value: applicationId });
    return conditions;
  }, [categoryId, applicationId]);

  useEffect(() => {
    const fetchPresentations = async () => {
      setIsLoading(true);
      try {
        if (whereConditions.length > 0) {
          const data = await findAllPresentationsByQuery(whereConditions, []);
          setPresentations(data);
        }
      } catch (error) {
        console.error("Błąd podczas pobierania prezentacji:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPresentations();
  }, [whereConditions]);

  // Orkiestracja procesów - dodawanie prezentacji
  const handlePresentationAdd = async (
    presentationData: PresentationWithId
  ): Promise<OperationResult> => {
    try {
      if (!application) {
        return { success: false, error: "Brak aplikacji" };
      }

      // 1. Przygotuj nową mapę uczestników
      const allPresentations = [...presentations, presentationData];
      const newParticipantPresentationsMap: { [key: string]: number } = {};

      allPresentations.forEach((pres) => {
        pres.participants?.forEach((participant) => {
          if (participant.id) {
            newParticipantPresentationsMap[participant.id] =
              (newParticipantPresentationsMap[participant.id] || 0) + 1;
          }
        });
      });

      // ============================================================================
      // OBLICZANIE NOWEJ MAPY UCZESTNIKÓW
      // ============================================================================
      // Tworzy mapę uczestników uwzględniającą nową prezentację
      // Każdy uczestnik może mieć wiele prezentacji - mapa zlicza ich liczbę

      const updatedApplication = {
        ...application,
        participantPresentationsMap: newParticipantPresentationsMap,
      };

      // 2. Zapis do Firestore
      const createdPresentation = await createPresentationAndUpdateParticipantsCounter(
        presentationData,
        presentationData.userId,
        updatedApplication
      );

      if (!createdPresentation) {
        return { success: false, error: "Nie udało się utworzyć prezentacji" };
      }

      // ============================================================================
      // OBLICZANIE I AKTUALIZACJA KWOT
      // ============================================================================
      // Przelicza kwoty zgłoszenia na podstawie nowej mapy uczestników
      // Uwzględnia wszystkie prezentacje i ich uczestników
      const applicationWithSum = await calculatePaymentForApplication(updatedApplication);
      await updateApplication(application.id!, applicationWithSum);

      // 4. Logowanie
      try {
        await logPresentationCreate(createdPresentation, true, undefined, {
          applicationId: createdPresentation.applicationId,
          categoryId: createdPresentation.categoryId,
          contestId: createdPresentation.contestId,
        });
      } catch (loggingError) {
        console.warn("Błąd logowania (ignorowany):", loggingError);
      }

      // 5. Aktualizacja stanu
      setPresentations((prev) => [...prev, createdPresentation]);

      return { success: true, presentation: createdPresentation };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      try {
        await logPresentationCreate(presentationData, false, errorMessage, {
          applicationId: presentationData.applicationId,
          categoryId: presentationData.categoryId,
          contestId: presentationData.contestId,
        });
      } catch (loggingError) {
        console.warn("Błąd logowania błędu (ignorowany):", loggingError);
      }
      return { success: false, error: errorMessage };
    }
  };

  // Orkiestracja procesów - aktualizacja prezentacji
  const handlePresentationUpdate = async (
    presentationId: string,
    updatedData: Partial<PresentationWithId>
  ): Promise<OperationResult> => {
    try {
      if (!application) {
        return { success: false, error: "Brak aplikacji" };
      }

      // 1. Znajdź stary stan
      const oldPresentation = presentations.find((p) => p.id === presentationId);
      if (!oldPresentation) {
        return { success: false, error: "Prezentacja nie została znaleziona" };
      }

      // 2. Przygotuj nowy stan
      const newPresentation = { ...oldPresentation, ...updatedData };

      // 3. Przygotuj nową mapę uczestników
      const allPresentations = presentations.map((p) =>
        p.id === presentationId ? newPresentation : p
      );
      const newParticipantPresentationsMap: { [key: string]: number } = {};

      allPresentations.forEach((pres) => {
        pres.participants?.forEach((participant) => {
          if (participant.id) {
            newParticipantPresentationsMap[participant.id] =
              (newParticipantPresentationsMap[participant.id] || 0) + 1;
          }
        });
      });

      const updatedApplication = {
        ...application,
        participantPresentationsMap: newParticipantPresentationsMap,
      };

      // 4. Aktualizuj w Firestore
      await updatePresentationAndParticipantsCounter(newPresentation, updatedApplication);

      // 5. Aktualizacja zgłoszenia z nową sumą
      const applicationWithSum = await calculatePaymentForApplication(updatedApplication);
      await updateApplication(application.id!, applicationWithSum);

      // 6. Aktualizuj stan lokalny
      setPresentations((prev) => prev.map((p) => (p.id === presentationId ? newPresentation : p)));

      // 7. Logowanie
      try {
        await logPresentationUpdate(oldPresentation, newPresentation, true, undefined, {
          applicationId: newPresentation.applicationId,
          categoryId: newPresentation.categoryId,
          contestId: newPresentation.contestId,
        });
      } catch (loggingError) {
        console.warn("Błąd logowania (ignorowany):", loggingError);
      }

      return { success: true, presentation: newPresentation };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      const oldPresentation = presentations.find((p) => p.id === presentationId);
      if (oldPresentation) {
        try {
          await logPresentationUpdate(
            oldPresentation,
            { ...oldPresentation, ...updatedData },
            false,
            errorMessage,
            {
              applicationId: oldPresentation.applicationId,
              categoryId: oldPresentation.categoryId,
              contestId: oldPresentation.contestId,
            }
          );
        } catch (loggingError) {
          console.warn("Błąd logowania błędu (ignorowany):", loggingError);
        }
      }
      return { success: false, error: errorMessage };
    }
  };

  // Orkiestracja procesów - usunięcie prezentacji
  const handlePresentationDelete = async (presentationId: string): Promise<OperationResult> => {
    try {
      if (!application) {
        return { success: false, error: "Brak aplikacji" };
      }

      // 1. Znajdź prezentację
      const presentationToDelete = presentations.find((p) => p.id === presentationId);
      if (!presentationToDelete) {
        return { success: false, error: "Prezentacja nie została znaleziona" };
      }

      // 2. Przygotuj nową mapę uczestników
      const remainingPresentations = presentations.filter((p) => p.id !== presentationId);
      const newParticipantPresentationsMap: { [key: string]: number } = {};

      remainingPresentations.forEach((pres) => {
        pres.participants?.forEach((participant) => {
          if (participant.id) {
            newParticipantPresentationsMap[participant.id] =
              (newParticipantPresentationsMap[participant.id] || 0) + 1;
          }
        });
      });

      // ============================================================================
      // OBLICZANIE NOWEJ MAPY UCZESTNIKÓW PO USUNIĘCIU
      // ============================================================================
      // Tworzy mapę uczestników po usunięciu prezentacji
      // Uwzględnia tylko pozostałe prezentacje

      const updatedApplication = {
        ...application,
        participantPresentationsMap: newParticipantPresentationsMap,
      };

      // 3. Usuń z Firestore
      await deletePresentationAndUpdateParticipantsCounter(
        presentationToDelete,
        presentationToDelete.userId,
        updatedApplication
      );

      // ============================================================================
      // OBLICZANIE I AKTUALIZACJA KWOT PO USUNIĘCIU
      // ============================================================================
      // Przelicza kwoty zgłoszenia po usunięciu prezentacji
      // Uwzględnia tylko pozostałe prezentacje i ich uczestników
      const applicationWithSum = await calculatePaymentForApplication(updatedApplication);
      await updateApplication(application.id!, applicationWithSum);

      // 5. Aktualizuj stan lokalny
      setPresentations((prev) => prev.filter((p) => p.id !== presentationId));

      // 6. Logowanie
      try {
        await logPresentationDelete(presentationToDelete, true, undefined, {
          applicationId: presentationToDelete.applicationId,
          categoryId: presentationToDelete.categoryId,
          contestId: presentationToDelete.contestId,
        });
      } catch (loggingError) {
        console.warn("Błąd logowania (ignorowany):", loggingError);
      }

      return { success: true };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      const presentationToDelete = presentations.find((p) => p.id === presentationId);
      if (presentationToDelete) {
        try {
          await logPresentationDelete(presentationToDelete, false, errorMessage, {
            applicationId: presentationToDelete.applicationId,
            categoryId: presentationToDelete.categoryId,
            contestId: presentationToDelete.contestId,
          });
        } catch (loggingError) {
          console.warn("Błąd logowania błędu (ignorowany):", loggingError);
        }
      }
      return { success: false, error: errorMessage };
    }
  };

  /**
   * ============================================================================
   * FUNKCJA: handleSoloPresentationsBatch
   * ============================================================================
   * 
   * CEL FUNKCJI:
   * - Wykonuje operacje wsadowe na prezentacjach SOLO
   * - Obsługuje dodawanie i usuwanie wielu prezentacji w jednej transakcji
   * - Zapewnia spójność danych i poprawność obliczeń kwot
   * - Automatycznie odświeża dane z bazy po operacjach
   * 
   * ALGORYTM:
   * 1. Przygotowanie - analizuje stan przed operacjami
   * 2. Obliczanie mapy uczestników - uwzględnia wszystkie zmiany
   * 3. Wykonanie operacji - usuwa stare, dodaje nowe
   * 4. Aktualizacja kwot - przelicza na podstawie nowej mapy
   * 5. Odświeżenie danych - pobiera aktualne dane z bazy
   * 
   * SPECYFIKA SOLO:
   * - Operacje wsadowe - wszystkie zmiany w jednej transakcji
   * - Automatyczne odświeżanie danych z bazy po operacjach
   * - Poprawne obliczanie mapy uczestników i kwot
   * - Obsługa duplikatów kluczy React
   * 
   * PARAMETRY WEJŚCIOWE:
   * - presentationsToAdd: Lista prezentacji do dodania
   * - presentationsToDelete: Lista prezentacji do usunięcia
   * 
   * ZWRACA:
   * - OperationResult: Wynik operacji (success/error)
   */
  const handleSoloPresentationsBatch = async (
    presentationsToAdd: PresentationWithId[],
    presentationsToDelete: PresentationWithId[]
  ): Promise<OperationResult> => {
    try {
      if (!application) {
        return { success: false, error: "Brak aplikacji" };
      }

      // ============================================================================
      // PRZYGOTOWANIE DO OPERACJI WSADOWYCH SOLO
      // ============================================================================
      // Analizuje stan przed wykonaniem operacji wsadowych
      // Uwzględnia wszystkie zmiany w jednej transakcji

      // 1. Przygotuj nową mapę uczestników uwzględniając wszystkie zmiany
      const remainingPresentations = presentations.filter(
        (p) => !presentationsToDelete.some((toDelete) => toDelete.id === p.id)
      );
      const allPresentations = [...remainingPresentations, ...presentationsToAdd];

      const newParticipantPresentationsMap: { [key: string]: number } = {};
      allPresentations.forEach((pres) => {
        pres.participants?.forEach((participant) => {
          if (participant.id) {
            newParticipantPresentationsMap[participant.id] =
              (newParticipantPresentationsMap[participant.id] || 0) + 1;
          }
        });
      });

      // ============================================================================
      // OBLICZANIE KOŃCOWEJ MAPY UCZESTNIKÓW
      // ============================================================================
      // Tworzy mapę uczestników uwzględniającą wszystkie zmiany wsadowe
      // Uwzględnia usunięte i dodane prezentacje w jednym obliczeniu

      const updatedApplication = {
        ...application,
        participantPresentationsMap: newParticipantPresentationsMap,
      };

      // ============================================================================
      // WYKONANIE OPERACJI USUWANIA
      // ============================================================================
      // Usuwa prezentacje w kolejności - najpierw usunięcie, potem dodanie
      // Zapewnia spójność danych w bazie
      for (const presentationToDelete of presentationsToDelete) {
        if (presentationToDelete.id) {
          await deletePresentationAndUpdateParticipantsCounter(
            presentationToDelete,
            presentationToDelete.userId,
            updatedApplication
          );
        }
      }

      // ============================================================================
      // WYKONANIE OPERACJI DODAWANIA
      // ============================================================================
      // Dodaje nowe prezentacje po usunięciu starych
      // Każda prezentacja SOLO = jeden zawodnik
      for (const presentationToAdd of presentationsToAdd) {
        await createPresentationAndUpdateParticipantsCounter(
          presentationToAdd,
          presentationToAdd.userId,
          updatedApplication
        );
      }

      // ============================================================================
      // OBLICZANIE I AKTUALIZACJA KWOT PO OPERACJACH WSADOWYCH
      // ============================================================================
      // Przelicza kwoty zgłoszenia po wszystkich zmianach wsadowych
      // Uwzględnia końcowy stan mapy uczestników
      const applicationWithSum = await calculatePaymentForApplication(updatedApplication);
      await updateApplication(application.id!, applicationWithSum);

      // 5. Aktualizuj stan lokalny - usuń tylko te do usunięcia
      setPresentations((prev) => {
        return prev.filter((p) => !presentationsToDelete.some((toDelete) => toDelete.id === p.id));
      });

      // ============================================================================
      // ODSWIEŻENIE DANYCH Z BAZY
      // ============================================================================
      // Wymusza pobranie aktualnych danych z bazy po operacjach wsadowych
      // Zapewnia spójność lokalnego stanu z rzeczywistym stanem bazy
      try {
        const data = await findAllPresentationsByQuery(whereConditions, []);
        setPresentations(data);
      } catch (error) {
        // Błąd odświeżania nie blokuje operacji - dane zostaną zsynchronizowane przy następnym odświeżeniu
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("❌ Błąd podczas operacji wsadowych SOLO:", errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  return {
    // Stan
    presentations,
    isLoading,

    // Orkiestrowane metody
    handlePresentationAdd,
    handlePresentationUpdate,
    handlePresentationDelete,
    handleSoloPresentationsBatch,
  };
};
